import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import AppContext from '../AppContext.jsx';
import dbClient from '../db/client.js';
import auth from '../db/auth.js';
import { Row, Col } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import DataTable from 'react-data-table-component';
import { formatStatus, statusColor } from '../helpers.js';

function OfflineOrdersTable({ showExport = false, showDelete = false, showTitle = true }) {
  const { app } = useContext(AppContext);
  const navigate = useNavigate();
  const [offlineOrders, setOfflineOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);

  useEffect(() => {
    loadOfflineOrders();
  }, []);

  async function loadOfflineOrders() {
    try {
      const locationId = await auth.getLocationIdAsync();
      if (!locationId) {
        console.log("Cannot load offline orders - no location_id set");
        return;
      }

      const db = await dbClient.open();
      const indexName = `location_id, created_at`;
      const index = db.transaction("offline_orders").store.index(indexName);
      const offlineRange = IDBKeyRange.bound([locationId, ""], [locationId, "\uffff"]);
      const newOfflineOrders = await index.getAll(offlineRange);
      setOfflineOrders([...newOfflineOrders]);
    } catch (error) {
      console.error("Error loading offline orders:", error);
    } finally {
      setLoading(false);
    }
  }

  async function deleteAllOfflineOrders() {
    try {
      const locationId = auth.getLocationId();
      if (!locationId) {
        console.log("Cannot delete offline orders - no location_id set");
        return;
      }

      const db = await dbClient.open();
      const transaction = db.transaction("offline_orders", "readwrite");
      const store = transaction.objectStore("offline_orders");
      const indexName = `location_id, created_at`;
      const index = store.index(indexName);
      const offlineRange = IDBKeyRange.bound([locationId, ""], [locationId, "\uffff"]);
      const keys = await index.getAllKeys(offlineRange);

      // Delete all orders for this location
      for (const key of keys) {
        await store.delete(key);
      }

      // Reload the orders list
      await loadOfflineOrders();

      setModalConfig({
        title: "Success",
        body: `Successfully deleted ${keys.length} offline orders.`,
        primaryButtonText: "OK",
        primaryButtonHandler: handleCloseModal
      });
      handleShowModal();
    } catch (error) {
      console.error("Error deleting offline orders:", error);
      setModalConfig({
        title: "Error",
        body: "Failed to delete offline orders. Please try again.",
        primaryButtonText: "OK",
        primaryButtonHandler: handleCloseModal
      });
      handleShowModal();
    }
  }

  function showDeleteModal() {
    setModalConfig({
      title: "Delete All Offline Orders",
      body: `Are you sure you want to delete all ${offlineOrders.length} unsynced offline orders? This action cannot be undone.`,
      primaryButtonText: "Delete All Orders",
      primaryButtonHandler: deleteAllOfflineOrders
    });
    handleShowModal();
  }

  function handleCloseModal() {
    setShowModal(false);
  }

  function handleShowModal() {
    setShowModal(true);
  }

  function statusCellElement(row) {
    let bgColor = statusColor(row.status);

    return (
      <>
        <div data-tag="allowRowEvents" className="px-3 py-1 rounded-pill text-white" style={{ backgroundColor: bgColor }}>
          {formatStatus(row.status)}
        </div>
      </>
    );
  }

  const columns = [
    {
      id: "master",
      name: 'Master',
      selector: row => row.master,
      sortable: true,
      sortField: 'master',
      width: "150px"
    },
    {
      id: "status",
      name: 'Status',
      selector: row => formatStatus(row.status),
      sortable: false,
      sortField: 'status',
      width: "150px",
      cell: statusCellElement
    },
    {
      id: "first_name",
      name: 'First Name',
      selector: row => row.bill_same_as_ship ? row.ship_to.first_name : row.bill_to.first_name,
      sortable: true,
      hide: 992,
      sortField: 'ship_first_name',
      minWidth: "50px"
    },
    {
      id: "last_name",
      name: 'Last Name',
      selector: row => row.bill_same_as_ship ? row.ship_to.last_name : row.bill_to.last_name,
      sortable: true,
      sortField: 'ship_last_name',
      minWidth: "50px"
    },
    {
      id: "fish_tag",
      name: 'Fish Tag',
      selector: row => row.fish_tag,
      sortable: true,
      sortField: 'fish_tag',
      minWidth: "50px"
    },
    {
      id: "trip_code",
      name: 'Trip Code',
      selector: row => row.trip_code,
      sortable: true,
      sortField: 'trip_code',
      minWidth: "50px"
    },
    {
      id: "order_date",
      name: "Order Date",
      selector: row => {
        return row.ordered_at.split('T')[0];
      },
      sortable: true,
      sortField: 'ordered_at',
      width: "140px"
    },
  ];

  function handleRowClicked(row) {
    navigate(`/orders/${row.id}`);
  }

  if (offlineOrders?.length === 0) {
    return null;
  }

  return (
    <>
      {showTitle && (
        <Row className="mt-4 mb-2">
          <Col>
            <span className="fw-bold text-secondary">OFFLINE ORDERS</span>
          </Col>
          <Col xs="auto">
            <div className="d-flex gap-2">
              {showExport && (
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => {
                    const dataStr = JSON.stringify(offlineOrders, null, 2);
                    const dataBlob = new Blob([dataStr], { type: 'application/json' });
                    const url = URL.createObjectURL(dataBlob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `offline-orders-${new Date().toISOString().split('T')[0]}.json`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                >
                  Export
                </Button>
              )}
              {showDelete && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={showDeleteModal}
                >
                  Delete All
                </Button>
              )}
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <DataTable
          columns={columns}
          data={offlineOrders}
          progressPending={loading}
          onRowClicked={handleRowClicked}
          responsive={true}
          customStyles={{
            headRow: {
              style: {
                backgroundColor: "#fcefdc !important"
              }
            }
          }}
        />
      </Row>
      {modalConfig && (
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{modalConfig.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modalConfig.body}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="danger" onClick={modalConfig.primaryButtonHandler}>
              {modalConfig.primaryButtonText}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

export default OfflineOrdersTable; 